import {
  customerDisplayName,
  isNameUnsure,
} from "@/features/customers/name-verification";
import {
  SearchableSelect,
  type SearchableSelectItem,
} from "@/components/molecules/searchable-select";
import { UserAvatar } from "@/components/molecules/user-avatar";
import { useCurrentClient } from "@/features/clients/hooks/use-clients";
import { useCustomers } from "@/features/customers/hooks/use-customers";
import { useDebounce } from "@/features/utils/use-debounce";
import { UserIcon } from "@phosphor-icons/react";
import { useMemo, useState } from "react";
import { useTranslation } from "@/features/utils/i18n";

interface CustomerSelectProps {
  value: string; // Customer ID
  onChange: (value: string) => void;
  placeholder?: string;
  disabled?: boolean;
  allowNone?: boolean;
}

export function CustomerSelect({
  value,
  onChange,
  placeholder,
  disabled = false,
  allowNone = false,
}: CustomerSelectProps) {
  const { t } = useTranslation();
  const clientId = useCurrentClient()?.client.id || "";
  const [searchQuery, setSearchQuery] = useState("");
  
  // Debounce search to avoid too many API calls
  const debouncedSearch = useDebounce(searchQuery, 300);

  // Fetch customers with server-side search
  const { data: customersData, isLoading } = useCustomers(clientId, {
    search: debouncedSearch || undefined,
    limit: 50, // Reasonable limit since we have server-side search
  });

  const customers = customersData?.customers || [];

  // Convert to SearchableSelectItem format
  const items: SearchableSelectItem[] = useMemo(
    () =>
      customers.map((customer) => {
        const primaryEmail =
          customer.addresses?.email?.find((e) => e.primary)?.address ||
          customer.addresses?.email?.[0]?.address;

        const primaryPhone =
          customer.addresses?.phone?.find((p) => p.primary)?.number ||
          customer.addresses?.phone?.[0]?.number;

        const name = customerDisplayName(customer);

        // Build description from email or phone
        const description = primaryEmail || primaryPhone || undefined;

        return {
          id: customer.id,
          // A plain string (it is also what the search matches), so the "?" of a
          // name only heard on a call is written into it.
          label: name
            ? `${name}${isNameUnsure(customer) ? " ?" : ""}`
            : t("common.unknown"),
          description,
          icon: (
            <UserAvatar
              avatar={customer.photo_url}
              name={name}
              email={primaryEmail}
              size="sm"
            />
          ),
          data: customer,
        };
      }),
    [customers],
  );

  return (
    <SearchableSelect
      items={items}
      value={value}
      onChange={(v) => onChange(Array.isArray(v) ? v[0] || "" : v)}
      multiple={false}
      placeholder={placeholder ?? t("common.select.customer.placeholder")}
      searchPlaceholder={t("common.select.customer.search")}
      emptyText={
        isLoading
          ? t("common.select.searching")
          : t("common.select.customer.empty")
      }
      isLoading={isLoading}
      disabled={disabled}
      allowNone={allowNone}
      onSearchChange={setSearchQuery}
      noneOption={
        allowNone
          ? {
              id: "",
              label: t("common.select.customer.none"),
              description: t("common.select.customer.noneDescription"),
              icon: <UserIcon className="h-4 w-4" />,
            }
          : undefined
      }
    />
  );
}
